import { useTheme } from '@repo/theme';
import { Banner } from 'component/banner';
import { Pad } from 'component/basics';
import { FormField } from 'component/form';
import { FunctionalModal } from 'component/modal/functional-modal';
import { RichText } from 'component/richtext';
import { Heading, TextArea } from 'component/text';
import { useState } from 'react';
import { t } from 'util/stdtypes';

export type SendToModeratorModalProps = {
    onClose: () => void;
    onSubmit: (appealText: string) => Promise<void>;
};

export function SendToModeratorModal({ onClose, onSubmit }: SendToModeratorModalProps) {
    const { colors } = useTheme();
    const [appealText, setAppealText] = useState('');
    const [inProgress, setInProgress] = useState(false);

    async function onReport() {
        setInProgress(true);
        await onSubmit(appealText);
        setInProgress(false);
    }

    return (
        <FunctionalModal
            content={
                <>
                    <Banner color={colors.colorSurfaceBannerInfo}>
                        <Heading color={colors.colorTextPrimary} label={t('moderation.use_ai_title', '✨ We use AI for moderation')} />
                        <Pad size={16} />
                        <RichText
                            color={colors.colorTextPrimary}
                            label={t(
                                'moderation.ai_mistake_explanation',
                                'AI helps us moderate comments, but sometimes it can make a mistake. If AI is preventing you from sharing a respectful opinion, send your comment to a moderator for review.',
                            )}
                        />
                    </Banner>
                    <FormField
                        descriptionLabel={t(
                            'moderation.provide_details_help',
                            'Provide any details to help us determine if your post follows our Community Guidelines.',
                        )}
                        label={t('moderation.share_details', 'Share details')}
                        required
                    >
                        <TextArea
                            onChange={setAppealText}
                            placeholderLabel={t('moderation.message_to_moderator_placeholder', 'Message to Moderator...')}
                            testID='appeal'
                            value={appealText}
                        />
                    </FormField>
                </>
            }
            headingLabel={t('moderation.send_to_moderator_title', 'Send to moderator')}
            onClose={onClose}
            primaryButton={{
                disabled: inProgress || !appealText,
                label: inProgress ? t('moderation.sending', 'Sending...') : t('moderation.send_to_moderator_button', 'Send to moderator'),
                onPress: onReport,
            }}
            secondaryButton={{
                label: t('action.cancel', 'Cancel'),
                onPress: onClose,
            }}
        />
    );
}
